import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import type {PersonnelResponse} from 'src/api/ProactApiClient';
import {AuthenticationTypeEnum, UserStatusEnum} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {
  useActivateUserMutation,
  useDeactivateUserMutation,
  useDisableMfaMutation,
  useDisableNotificationEmailsMutation,
  useEnableNotificationEmailsMutation,
  useGetSesSuppressionStatusQuery,
  useLockUserMutation,
  useRemoveSesSuppressionMutation,
  useResolveEmailMismatchMutation,
  useSyncCognitoUserMutation,
} from 'src/redux/queries/personnel';
import {useCheckUserStatusQuery} from 'src/redux/queries/user';
import {useAlert} from 'src/shared/hooks/useAlert';
import {usePrimaryInstanceLock} from 'src/shared/hooks/usePrimaryInstanceLock';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AccessCard} from './AccessCard';
import {EmailDeliveryCard} from './EmailDeliveryCard';
import {NotificationEmailsCard} from './NotificationEmailsCard';
import {SignInCard} from './SignInCard';
import {hasVerifiedMfaStatus} from './loginStatus';

type UpdateAction =
  | 'activate'
  | 'lock'
  | 'deactivate'
  | 'resync'
  | 'remove-suppression'
  | 'disable-mfa'
  | 'enable-notification-emails'
  | 'disable-notification-emails'
  | 'use-login-email';

type ConfirmAction = Extract<
  UpdateAction,
  | 'lock'
  | 'deactivate'
  | 'resync'
  | 'disable-mfa'
  | 'disable-notification-emails'
  | 'use-login-email'
>;

interface ConfirmCopy {
  title: string;
  body: string;
  yes: string;
  color: 'red' | 'yellow';
}

const confirmCopy = (
  name: string,
  loginEmail: string,
  ownProfileOnly: boolean
): Record<ConfirmAction, ConfirmCopy> => ({
  lock: {
    title: `Lock ${name}'s account?`,
    body: `${name} will have training-only access to this company until an administrator activates them again. This allows sign-in; it does not block the account. Access to other companies is unchanged. Their profile is kept.`,
    yes: 'Lock',
    color: 'yellow',
  },
  deactivate: {
    title: `Deactivate ${name}?`,
    body: `${name} loses access to this company, including work in progress. They may still sign in to other companies where they have access. Their profile and history are kept, but open or pending toolbox talks are cancelled and company group memberships are removed.`,
    yes: 'Deactivate',
    color: 'red',
  },
  resync: {
    title: `Resync ${name}'s login?`,
    body: `The shared login used across companies is deleted and recreated. ${name} must sign in again and normally receives a new invitation to set a password. Any authenticator setup must be repeated. Their Proact profile is kept. Check missing emails and try password reset before using this recovery action.`,
    yes: 'Resync login',
    color: 'red',
  },
  'disable-mfa': {
    title: 'Disable multi-factor authentication?',
    body: `This removes the authenticator requirement for ${name} and signs them out everywhere. Only continue if you have confirmed their identity.`,
    yes: 'Disable MFA',
    color: 'red',
  },
  'disable-notification-emails': {
    title: 'Turn off notification emails?',
    body: `This stops notification emails to ${name} from this company. Sign-in and password reset emails are unaffected. Only an administrator can turn notifications back on.`,
    yes: 'Turn off',
    color: 'red',
  },
  'use-login-email': {
    title: 'Use the login email?',
    body: ownProfileOnly
      ? `${name}'s profile in this company changes to ${loginEmail}. The login and the profiles in other companies do not change.`
      : `Every company profile for ${name} changes to ${loginEmail}. The login does not change. Notification emails go to this address.`,
    yes: 'Use login email',
    color: 'yellow',
  },
});

export interface PersonnelAuthenticationTabProps {
  item?: PersonnelResponse;
  onItemUpdated: (item: PersonnelResponse) => void;
}

export const PersonnelAuthenticationTab: React.FC<
  PersonnelAuthenticationTabProps
> = ({item, onItemUpdated}) => {
  const {setAlert} = useAlert();
  // Login, MFA and suppression live on the shared account, so only the primary instance edits them.
  const {isLocked, managedByName} = usePrimaryInstanceLock(item);

  const loginEmail = item?.accountEmail ?? item?.email ?? '';

  const [busy, setBusy] = useState<UpdateAction | null>(null);
  const [confirming, setConfirming] = useState<ConfirmAction | null>(null);

  const {
    currentData: loginStatus,
    isFetching: isLoadingLogin,
    error: loginError,
    refetch: refetchLogin,
  } = useCheckUserStatusQuery(
    {email: loginEmail},
    {skip: !loginEmail, refetchOnMountOrArgChange: true}
  );
  const {
    currentData: suppression,
    isFetching: isLoadingSuppression,
    error: suppressionError,
    refetch: refetchSuppression,
  } = useGetSesSuppressionStatusQuery(item?.id ?? 0, {
    skip: !item?.id,
    refetchOnMountOrArgChange: true,
  });

  const [activateUser] = useActivateUserMutation();
  const [lockUser] = useLockUserMutation();
  const [deactivateUser] = useDeactivateUserMutation();
  const [syncCognitoUser] = useSyncCognitoUserMutation();
  const [resolveEmailMismatch] = useResolveEmailMismatchMutation();
  const [removeSesSuppression] = useRemoveSesSuppressionMutation();
  const [disableMfa] = useDisableMfaMutation();
  const [enableNotificationEmails] = useEnableNotificationEmailsMutation();
  const [disableNotificationEmails] = useDisableNotificationEmailsMutation();

  const run = async (
    action: UpdateAction,
    work: (id: number) => Promise<void>
  ) => {
    if (item === undefined) return;
    setConfirming(null);
    setBusy(action);
    try {
      await work(item.id);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setBusy(null);
  };

  const applyUpdate = (resp: PersonnelResponse) => {
    onItemUpdated(resp);
    void refetchLogin();
  };

  const actions: Record<UpdateAction, () => Promise<void>> = {
    activate: () =>
      run('activate', async id => applyUpdate(await activateUser(id).unwrap())),
    lock: () =>
      run('lock', async id => applyUpdate(await lockUser(id).unwrap())),
    deactivate: () =>
      run('deactivate', async id =>
        applyUpdate(await deactivateUser(id).unwrap())
      ),
    resync: () =>
      run('resync', async id => {
        await syncCognitoUser(id).unwrap();
        setAlert(
          'Login resynced. Refresh the sign-in status to check the next step.',
          'success'
        );
        void refetchLogin();
      }),
    'remove-suppression': () =>
      run('remove-suppression', async id => {
        await removeSesSuppression(id).unwrap();
        setAlert('Email suppression removed successfully', 'success');
      }),
    'disable-mfa': () =>
      run('disable-mfa', async id => {
        await disableMfa(id).unwrap();
        setAlert('Multi-factor authentication disabled', 'success');
        void refetchLogin();
      }),
    'enable-notification-emails': () =>
      run('enable-notification-emails', async id => {
        onItemUpdated(await enableNotificationEmails(id).unwrap());
        setAlert(`Notification emails turned on for ${item?.name}`, 'success');
      }),
    'disable-notification-emails': () =>
      run('disable-notification-emails', async id => {
        onItemUpdated(await disableNotificationEmails(id).unwrap());
        setAlert(`Notification emails turned off for ${item?.name}`, 'success');
      }),
    'use-login-email': () =>
      run('use-login-email', async id => {
        applyUpdate(await resolveEmailMismatch(id).unwrap());
        setAlert(
          isLocked
            ? 'This profile now uses the login email'
            : 'Every company profile now uses the login email',
          'success'
        );
      }),
  };

  if (item === undefined) return null;

  const isPasswordLogin =
    loginStatus?.domainAuth.authType === AuthenticationTypeEnum.JWT;
  const hasLoginAccess =
    item.active === UserStatusEnum.Active ||
    item.active === UserStatusEnum.Pending;
  const canResync = !isLocked && isPasswordLogin && hasLoginAccess;
  const canDisableMfa =
    !isLocked &&
    isPasswordLogin &&
    loginStatus?.mfaEnabled === true &&
    hasVerifiedMfaStatus(loginStatus.status);

  const confirm = confirming
    ? confirmCopy(item.name, loginEmail, isLocked)[confirming]
    : null;

  return (
    <Stack gap="lg">
      {isLocked && (
        <InfoAlert>
          Login, multi-factor authentication, and email suppression are managed
          by {managedByName}.
        </InfoAlert>
      )}

      <SimpleGrid cols={{base: 1, lg: 2}} spacing="lg">
        <SignInCard
          email={item.email}
          loginEmail={loginEmail}
          hasEmailMismatch={item.hasEmailMismatch === true}
          onUseLoginEmail={() => setConfirming('use-login-email')}
          usingLoginEmail={busy === 'use-login-email'}
          status={loginStatus}
          isLoading={isLoadingLogin}
          errorMessage={loginError ? parseErrorMessage(loginError) : undefined}
          managedByName={isLocked ? managedByName : undefined}
          onRefresh={() => void refetchLogin()}
          canResync={canResync}
          canDisableMfa={canDisableMfa}
          onResync={() => setConfirming('resync')}
          onDisableMfa={() => setConfirming('disable-mfa')}
          resyncing={busy === 'resync'}
          disablingMfa={busy === 'disable-mfa'}
          disabled={busy !== null}
        />
        <AccessCard
          active={item.active}
          onActivate={() => void actions.activate()}
          onLock={() => setConfirming('lock')}
          onDeactivate={() => setConfirming('deactivate')}
          activating={busy === 'activate'}
          locking={busy === 'lock'}
          deactivating={busy === 'deactivate'}
          disabled={busy !== null}
        />
        {item.notificationEmailsEnabled !== undefined && (
          <NotificationEmailsCard
            enabled={item.notificationEmailsEnabled}
            onTurnOn={() => void actions['enable-notification-emails']()}
            onTurnOff={() => setConfirming('disable-notification-emails')}
            turningOn={busy === 'enable-notification-emails'}
            turningOff={busy === 'disable-notification-emails'}
            disabled={busy !== null}
          />
        )}
        <EmailDeliveryCard
          suppression={suppression}
          isLoading={isLoadingSuppression}
          errorMessage={
            suppressionError ? parseErrorMessage(suppressionError) : undefined
          }
          onRefresh={() => void refetchSuppression()}
          canRemoveSuppression={!isLocked}
          onRemoveSuppression={() => void actions['remove-suppression']()}
          removing={busy === 'remove-suppression'}
          disabled={busy !== null}
        />
      </SimpleGrid>

      <ConfirmDialog
        opened={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && void actions[confirming]()}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmationButtonColor={confirm?.color}
        buttonTextYes={confirm?.yes}
        confirmationButtonDisabled={busy !== null}
      />
    </Stack>
  );
};
